import * as XLSX from 'xlsx';
import { normalizeTags, tagsToDisplay } from 'features/datasets/utils/tags';
import { importOriginFromFilename } from 'features/datasets/utils/collaboration';

/** The largest file the browser will read. The row count is not capped. */
export const MAX_IMPORT_BYTES = 25 * 1024 * 1024;

export const cleanCol = (c) => String(c || '').trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_.]/g, '').slice(0, 60);
const metaToStr = (m) => (m == null || m === '' ? '' : (typeof m === 'string' ? m : JSON.stringify(m)));
const displayVal = (v) => (v == null ? '' : (typeof v === 'string' ? v : JSON.stringify(v)));

const EXPECTED_COL_RE = /^(expected_output|expected|expected output|ground_truth|target)$/i;
/** Columns that are not inputs: the row's expected output, metadata and tags. */
export const RESERVED = new Set(['expected_output', 'expected', 'metadata']);

/** A readable message for a failed import: always an Error with text to show. */
export class ImportProblem extends Error {}

/**
 * Bytes to text. A byte order mark decides UTF-8 or UTF-16; no mark means UTF-8,
 * and bytes that are not valid UTF-8 are read as Windows-1252, which is what a
 * spreadsheet "CSV" export is on most machines. A wrong guess here is what
 * turns "café" into "cafÃ©", so it is tried in that order, never skipped.
 */
export function decodeText(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) return new TextDecoder('utf-8').decode(bytes.subarray(3));
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/** The delimiter the first line uses (comma, tab, semicolon), ignoring quoted text. */
export function detectDelimiter(text) {
  let inQuotes = false;
  const counts = { ',': 0, '\t': 0, ';': 0 };
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (c === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (c === '\n' || c === '\r')) break;
    else if (!inQuotes && c in counts) counts[c] += 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best[1] > 0 ? best[0] : ',';
}

/** RFC 4180 text to a matrix of strings: quotes, doubled quotes, embedded delimiters and newlines. */
export function parseDelimited(text) {
  const src = String(text || '').replace(/^﻿/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const sep = detectDelimiter(src);
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else { inQuotes = false; }
      } else { field += c; }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === sep) {
      row.push(field); field = '';
    } else if (c === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else {
      field += c;
    }
  }
  if (inQuotes) throw new ImportProblem('A quote in the file is never closed.');
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((cell) => String(cell).trim() !== ''));
}

function flattenImportRecord(rec) {
  if (!rec || typeof rec !== 'object' || Array.isArray(rec)) return null;
  const flat = {};
  const input = rec.input && typeof rec.input === 'object' && !Array.isArray(rec.input) ? rec.input : null;
  if (input) {
    for (const [k, v] of Object.entries(input)) flat[k] = displayVal(v);
  } else {
    for (const [k, v] of Object.entries(rec)) {
      if (k === 'expected_output' || k === 'expected' || k === 'metadata' || k === 'tags' || k === 'input') continue;
      flat[k] = displayVal(v);
    }
  }
  const exp = rec.expected_output ?? rec.expected;
  if (exp != null) flat.expected_output = displayVal(exp);
  if (rec.metadata != null) flat.metadata = metaToStr(rec.metadata);
  if (rec.tags != null) flat.tags = tagsToDisplay(rec.tags);
  return flat;
}

/** Records to a header + rows matrix; keys seen in any record become columns. */
export function recordsToMatrix(records) {
  const list = Array.isArray(records) ? records : [records];
  const flats = list.map(flattenImportRecord).filter(Boolean);
  const skipped = list.length - flats.length;
  if (!flats.length) throw new ImportProblem('No records found. Each one must be an object.');
  const header = [];
  const seen = new Set();
  for (const flat of flats) {
    for (const k of Object.keys(flat)) {
      if (!seen.has(k)) { seen.add(k); header.push(k); }
    }
  }
  return {
    header,
    dataRows: flats.map((flat) => header.map((h) => flat[h] ?? '')),
    warnings: skipped ? [`${skipped} ${skipped === 1 ? 'entry was' : 'entries were'} not an object and skipped.`] : [],
  };
}

/** An array of records, one record, or { items | rows | data | records: [...] }. */
export function parseJsonImport(text) {
  let data;
  try { data = JSON.parse(String(text).replace(/^﻿/, '')); } catch (e) { throw new ImportProblem(`Not valid JSON: ${e.message}`); }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const wrapped = ['items', 'rows', 'data', 'records'].find((k) => Array.isArray(data[k]));
    if (wrapped) data = data[wrapped];
  }
  if (Array.isArray(data) && !data.length) throw new ImportProblem('The file has no records.');
  return recordsToMatrix(data);
}

export function parseJsonlImport(text) {
  const lines = String(text).replace(/^﻿/, '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) throw new ImportProblem('The file is empty.');
  const records = lines.map((line, i) => {
    try { return JSON.parse(line); } catch { throw new ImportProblem(`Invalid JSON on line ${i + 1}.`); }
  });
  return recordsToMatrix(records);
}

/**
 * A header + rows matrix from a text table. The header is made usable (blank
 * names become column_N, repeats get _2, _3) and every row is made the header's
 * width, so a ragged row cannot shift a value into the next column's place.
 */
export function matrixToTable(matrix) {
  if (!matrix.length) throw new ImportProblem('The file is empty.');
  if (matrix.length < 2) throw new ImportProblem('The file has a header row but no data rows.');
  const used = new Map();
  const header = matrix[0].map((h, i) => {
    const base = String(h ?? '').trim() || `column_${i + 1}`;
    const n = (used.get(base) || 0) + 1;
    used.set(base, n);
    return n === 1 ? base : `${base}_${n}`;
  });
  let ragged = 0;
  const dataRows = matrix.slice(1)
    .filter((r) => r.some((c) => String(c ?? '').trim() !== ''))
    .map((r) => {
      if (r.length !== header.length) ragged += 1;
      return header.map((_, i) => (r[i] == null ? '' : String(r[i])));
    });
  const warnings = ragged ? [`${ragged} ${ragged === 1 ? 'row has' : 'rows have'} a different number of cells than the header; missing cells are empty and extras are dropped.`] : [];
  return { header, dataRows, warnings };
}

export function parseSpreadsheet(buffer) {
  let wb;
  try { wb = XLSX.read(buffer, { type: 'array' }); } catch { throw new ImportProblem('This file could not be read as a spreadsheet.'); }
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws) throw new ImportProblem('The spreadsheet has no sheets.');
  return matrixToTable(XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' }));
}

/**
 * Read a File into { header, dataRows, warnings } or throw an ImportProblem
 * whose message can be shown as is. Never returns an empty table.
 */
export async function parseImportFile(file) {
  if (!file) throw new ImportProblem('No file chosen.');
  if (file.size === 0) throw new ImportProblem('The file is empty.');
  if (file.size > MAX_IMPORT_BYTES) throw new ImportProblem(`The file is ${(file.size / 1048576).toFixed(1)} MB; the limit is ${MAX_IMPORT_BYTES / 1048576} MB.`);
  const buf = await file.arrayBuffer();
  const lower = String(file.name || '').toLowerCase();
  let table;
  if (lower.endsWith('.json')) table = parseJsonImport(decodeText(buf));
  else if (lower.endsWith('.jsonl') || lower.endsWith('.ndjson')) table = parseJsonlImport(decodeText(buf));
  else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) table = parseSpreadsheet(buf);
  else table = matrixToTable(parseDelimited(decodeText(buf)));
  if (!table.dataRows.length) throw new ImportProblem('The file has no data rows.');
  return { ...table, warnings: table.warnings || [] };
}

/** Where each file column goes by default: a known first-class section, a matching dataset column, or a new one. */
export function buildImportMapping(header, columns) {
  return header.map((h) => {
    const clean = cleanCol(h);
    const lc = String(h).trim().toLowerCase();
    if (EXPECTED_COL_RE.test(lc)) return '__expected';
    if (lc === 'metadata') return '__metadata';
    if (lc === 'tags' || lc === 'tag') return '__tags';
    return columns.includes(clean) ? clean : (columns.includes(h) ? h : '__new__');
  });
}

/**
 * Apply a mapping: the columns the dataset ends up with and the rows to append.
 * Each row is { __id, __expected, __metadata, __tags, __origin, ...columns }.
 * Two file columns mapped to the same target keep the first non-empty value.
 */
export function buildImportedRows({ header, dataRows, mapping, columns, filename = null, newId }) {
  const origin = filename ? importOriginFromFilename(filename) : null;
  const nextCols = [...columns];
  const targets = mapping.map((m, i) => {
    if (m === '__skip__') return null;
    if (m === '__expected' || m === '__metadata' || m === '__tags') return m;
    if (m === '__new__') {
      const name = cleanCol(header[i]) || `column_${nextCols.length + 1}`;
      if (RESERVED.has(name)) return name === 'metadata' ? '__metadata' : '__expected';
      if (!nextCols.includes(name)) nextCols.push(name);
      return name;
    }
    return m;
  });
  const rows = dataRows.map((cells) => {
    const row = { __id: newId(), __expected: '', __metadata: '', __comments: [], __tags: [], __origin: origin };
    targets.forEach((t, i) => {
      if (!t) return;
      const val = cells[i] != null ? String(cells[i]) : '';
      if (t === '__tags') row.__tags = normalizeTags([...row.__tags, ...normalizeTags(val)]);
      else if (row[t] === undefined || row[t] === '') row[t] = val;
    });
    return row;
  });
  return { columns: nextCols, rows };
}
